import { z } from "zod";
import { customFieldRowsSchema } from "@/features/custom-fields/form-schema";
import {
  authorableBookingModes,
  closesSaleAfterEnd,
  closesSaleAfterStart,
} from "@/domain/activity/rules";
import { saleEndAnchors } from "@/domain/activity/types";
import { maxSaleCloseOffsetMinutes, saleCloseCopy } from "@/features/activities/sale-close-copy";
import { requiredText } from "@/domain/validation";

/** An emptied number input registers as NaN, and NaN is how the partner says
 *  "no default" — the same thing an untouched field says. */
function blankToUndefined(value: unknown): unknown {
  return typeof value === "number" && Number.isNaN(value) ? undefined : value;
}

const optionalDefaultPrice = z.preprocess(
  blankToUndefined,
  z.number().nonnegative("Cena nesmí být záporná").optional(),
);

const optionalDefaultCapacity = z.preprocess(
  blankToUndefined,
  z.number().int("Zadejte celé číslo").positive("Počet míst musí být alespoň 1").optional(),
);

const optionalSaleLeadTimeHours = z.preprocess(
  blankToUndefined,
  z
    .number()
    .int("Zadejte celé číslo")
    .positive("Předstih musí být alespoň 1 hodina")
    .max(8760, "Nejvýše 8760 hodin (rok)")
    .optional(),
);

const optionalSaleEndOffsetMinutes = z.preprocess(
  blankToUndefined,
  z
    .number()
    .int("Zadejte celé číslo")
    .min(-maxSaleCloseOffsetMinutes, saleCloseCopy.offsetOutOfRange)
    .max(maxSaleCloseOffsetMinutes, saleCloseCopy.offsetOutOfRange)
    .optional(),
);

const optionalDefaultDurationMinutes = z.preprocess(
  blankToUndefined,
  z
    .number()
    .int("Zadejte celé číslo")
    .min(1, saleCloseCopy.durationBounds.min)
    .max(1440, saleCloseCopy.durationBounds.max)
    .optional(),
);

/** Form-level schema for ActivityFormDialog. The field names are the database
 *  column names the editor was first written against; `activity-input.ts` turns
 *  them into what the API takes. */
export const activityFormSchema = z
  .object({
    activity_name: requiredText({ missing: "Název šablony slev je povinný", maxLength: 100 }),
    activity_subcategory_id: z.number({ required_error: "Kategorie je povinná" }),
    booking_mode: z.enum(authorableBookingModes, {
      required_error: "Vyberte, jak si zákazník slevový termín rezervuje",
    }),
    price_original: z.number({ required_error: "Původní cena je povinná" }).min(0, "Cena musí být kladná"),
    image_url: z.string({ required_error: "Obrázek je povinný" }).min(1, "Obrázek je povinný"),
    description: z.string().max(2000, "Max 2000 znaků").or(z.literal("")).optional(),
    custom_fields: customFieldRowsSchema,
    default_price_discounted: optionalDefaultPrice,
    default_capacity: optionalDefaultCapacity,
    sale_lead_time_hours: optionalSaleLeadTimeHours,
    sale_end_offset_minutes: optionalSaleEndOffsetMinutes,
    sale_end_anchor: z.enum(saleEndAnchors).optional(),
    default_duration_minutes: optionalDefaultDurationMinutes,
    requires_confirmation: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const { sale_end_offset_minutes: offsetMinutes, sale_end_anchor: anchor } = data;
    if ((offsetMinutes === undefined) !== (anchor === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: saleCloseCopy.incomplete,
        path: ["sale_end_anchor"],
      });
    } else if (
      offsetMinutes !== undefined &&
      anchor !== undefined &&
      data.requires_confirmation &&
      closesSaleAfterStart({ offsetMinutes, anchor })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: saleCloseCopy.afterStartRefused,
        path: ["sale_end_anchor"],
      });
    }
    if (
      offsetMinutes !== undefined &&
      anchor !== undefined &&
      closesSaleAfterEnd({ offsetMinutes, anchor }, data.default_duration_minutes)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: anchor === "end" ? saleCloseCopy.afterEndRefused : saleCloseCopy.outlivesDuration,
        path: ["sale_end_offset_minutes"],
      });
    }
    if (
      data.default_price_discounted !== undefined &&
      data.default_price_discounted > data.price_original
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Zlevněná cena nesmí být vyšší než původní cena",
        path: ["default_price_discounted"],
      });
    }
  });

export type ActivityFormData = z.infer<typeof activityFormSchema>;
